import React, { useState, useEffect } from "react";
import api from "../api/api.js";

export default function PhotoDetailModal({
  photoId,
  isOpen,
  onClose,
  onDelete,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [photo, setPhoto] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (isOpen && photoId) {
      const fetchPhoto = async () => {
        try {
          setLoading(true);
          setError("");
          setImageLoading(true);
          setImageError(false);

          const response = await api.get(`/photos/${photoId}`);
          setPhoto(response.data);
        } catch (err) {
          console.error("Error loading photo detail:", err);
          if (err.response?.status === 403) {
            setError(
              "Access denied. You don't have permission to view this photo."
            );
          } else if (err.response?.status === 404) {
            setError("Photo not found.");
          } else {
            setError("Failed to load photo. Please try again.");
          }
        } finally {
          setLoading(false);
        }
      };

      fetchPhoto();
    }
  }, [isOpen, photoId]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Determine which image URL to use (prefer medium, fallback to thumbnail, then original)
  const getImageUrl = () => {
    if (photo?.medium_url) return photo.medium_url;
    if (photo?.thumbnail_url) return photo.thumbnail_url;
    if (photo?.original_url) return photo.original_url;
    return null;
  };

  const imageUrl = getImageUrl();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Photo Details
            </h2>
            {photo && (
              <span className="text-sm text-gray-500">
                {new Date(photo.created_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
              <p className="text-gray-500">Loading photo...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="p-6 text-center">
              <div className="text-red-600 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-red-800 mb-2">
                Unable to Load Photo
              </h3>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={onClose}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {/* Photo Display */}
          {photo && !loading && !error && (
            <div>
              {/* Image Container */}
              <div className="relative bg-gray-100 flex items-center justify-center min-h-96 max-h-96">
                {imageUrl ? (
                  <>
                    {imageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                      </div>
                    )}

                    {imageError ? (
                      <div className="text-center text-gray-500 p-8">
                        <svg
                          className="w-16 h-16 mx-auto mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p>Failed to load image</p>
                      </div>
                    ) : (
                      <img
                        src={imageUrl}
                        alt={photo.description || `Photo ID ${photo.id}`}
                        className={`max-w-full max-h-96 object-contain transition-opacity duration-300 ${
                          imageLoading ? "opacity-0" : "opacity-100"
                        }`}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                      />
                    )}
                  </>
                ) : (
                  <div className="text-center text-gray-500 p-8">
                    <svg
                      className="w-16 h-16 mx-auto mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p>No image available</p>
                  </div>
                )}
              </div>

              {/* Photo Details */}
              <div className="p-6 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Photo ID:</span>
                        <span className="text-gray-900 font-mono text-sm">
                          {photo.id}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Created:</span>
                        <span className="text-gray-900">
                          {new Date(photo.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Dimensions:</span>
                        <span className="text-gray-900">
                          {photo.width && photo.height
                            ? `${photo.width} × ${photo.height} pixels`
                            : "Unknown"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            photo.status === "processed"
                              ? "bg-green-100 text-green-800"
                              : photo.status === "uploaded"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {photo.status}
                        </span>
                      </div>
                      {photo.content_type && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Type:</span>
                          <span className="text-gray-900">
                            {photo.content_type}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="mt-6">
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              "Are you sure you want to delete this photo?"
                            )
                          ) {
                            onDelete(photoId);
                          }
                        }}
                        className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                      >
                        Delete Photo
                      </button>
                    </div>
                  </div>

                  {/* Right Column - Available Versions */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Available Versions
                    </h3>
                    <div className="space-y-2">
                      {photo.thumbnail_url && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span>Thumbnail</span>
                        </div>
                      )}
                      {photo.medium_url && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span>Medium Resolution</span>
                        </div>
                      )}
                      {photo.original_url && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span>Original</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
